import { useEffect, FC } from 'react';
import { useNavigate, useLocation, matchPath, Outlet } from 'react-router-dom';

import {
  LOGOUT_PAGE_PATH,
  CONTRACT_MIGRATION_PAGE_PATH,
  WELCOME_PAGE_PATH,
  ACTIVATION_PAGE_PATH,
  INVITE_PAGE_PATH,
  LOCK_PAGE_PATH,
  LOCK_VPN_PAGE_PATH,
  LOGIN_PAGE_PATH,
} from 'application/consts';
import ActivationService from 'application/services/ActivationService';

import { useAccountIsReadyToUseLK, useAccountIsWithoutCompanies } from 'application/hooks';
import { createLogger } from '@shared/lib/logger';
import { useAppSelector } from '@shared/store';
import { selectAppBanned, selectAppInitialized } from '@app/store/application';
import {
  selectHasActiveContractMigration,
  selectIsAuthorized,
  selectUserId,
  selectCompanyIds,
} from '@entities/account';

import RouterUtils from '../utils';
import { AVAILABLE_TO_REDIRECT_ACTIVATION_FLOW_PATHS } from './consts';

const logger = createLogger('RedirectRoute', '#1fe66d');

const RedirectRoute: FC = () => {
  const hasAccess = useAppSelector(selectIsAuthorized);
  const userId = useAppSelector(selectUserId);
  const companiesCount = useAppSelector(selectCompanyIds).length;

  const isReadyToUseLK = useAccountIsReadyToUseLK();
  const isWithoutCompanies = useAccountIsWithoutCompanies();
  const isAppBanned = useAppSelector(selectAppBanned);
  const isAppInitialized = useAppSelector(selectAppInitialized);
  const hasActiveContractMigration = useAppSelector(selectHasActiveContractMigration);

  const navigate = useNavigate();

  const location = useLocation();

  const targetCurrentPathname = RouterUtils.readTargetPathname();

  const isLogoutPage = matchPath({ path: LOGOUT_PAGE_PATH, end: true }, location.pathname);
  const isContractMigrationPage = matchPath({ path: CONTRACT_MIGRATION_PAGE_PATH, end: true }, location.pathname);
  const isWelcomePage = matchPath({ path: WELCOME_PAGE_PATH, end: true }, location.pathname);
  const isLockVpnPage = matchPath({ path: LOCK_VPN_PAGE_PATH, end: true }, location.pathname);
  const isLockPage = matchPath({ path: LOCK_PAGE_PATH, end: true }, location.pathname);

  useEffect(() => {
    if (!isAppInitialized) {
      return;
    }

    logger.info('RedirectRoute useEffect check', {
      hasAccess,
      userDataId: userId,
      isWithoutCompanies,
      isReadyToUseLK,
      pathname: location.pathname,
    });

    if (isAppBanned) {
      if (!targetCurrentPathname) {
        RouterUtils.setTargetPathname(`${location.pathname}${location.search}`);
      }

      navigate(LOCK_VPN_PAGE_PATH, { replace: true });
      return;
    }

    if (hasAccess && isWithoutCompanies) {
      navigate(LOCK_PAGE_PATH, { replace: true });
      return;
    }

    if (!isAppBanned && isLockVpnPage) {
      navigate(targetCurrentPathname ?? LOGIN_PAGE_PATH, { replace: true });
      RouterUtils.clearTargetPathname();
    }

    if (!userId || isLogoutPage || isContractMigrationPage || isWelcomePage) {
      return;
    }

    if (!isWithoutCompanies && hasAccess) {
      if (hasActiveContractMigration) {
        navigate(CONTRACT_MIGRATION_PAGE_PATH, { replace: true });
        return;
      }

      if (!isReadyToUseLK && location.pathname !== INVITE_PAGE_PATH) {
        navigate(WELCOME_PAGE_PATH, { replace: true });
        return;
      }

      if (isLockPage) {
        navigate(targetCurrentPathname ?? LOGIN_PAGE_PATH, { replace: true });
        RouterUtils.clearTargetPathname();
        return;
      }
    }

    if (hasAccess && targetCurrentPathname) {
      RouterUtils.clearTargetPathname();
      navigate(targetCurrentPathname, { replace: true });
      return;
    }

    const isActivationFlow = ActivationService.isActivationFlow();

    if (isActivationFlow) {
      const isActivationMatch = AVAILABLE_TO_REDIRECT_ACTIVATION_FLOW_PATHS.find((path) =>
        matchPath({ path, end: false }, location.pathname)
      );

      navigate(isActivationMatch ? location.pathname : ACTIVATION_PAGE_PATH, { replace: true });
    }
  }, [
    isAppInitialized,
    isAppBanned,
    hasAccess,
    userId,
    companiesCount,
    location.pathname,
    targetCurrentPathname,
    isWithoutCompanies,
    isReadyToUseLK,
    hasActiveContractMigration,
    isLogoutPage,
    isContractMigrationPage,
    isWelcomePage,
  ]);

  return <Outlet />;
};

export default RedirectRoute;
